import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import 'assets/css/photoswipe-custom-style.css';
import type { Directive } from 'vue';
import type { SlideData } from 'photoswipe';

import SvgZoomClose from './assets/svg/zoom-close.svg?raw';

const viewImage = ({
  src,
  width,
  height,
  caption,
}: Pick<SlideData, 'height' | 'width' | 'src'> & { caption: string }) => {
  let lightbox: PhotoSwipeLightbox | null = new PhotoSwipeLightbox({
    dataSource: [{ src, width, height }],
    gallery: '#img-viewer',
    pswpModule: () => import('photoswipe'),
    zoom: false,
    closeSVG: SvgZoomClose,
    wheelToZoom: true,
    pinchToClose: false,
    closeOnVerticalDrag: false,
    initialZoomLevel: zoomLevelObject => {
      const padding = getPadding();
      const viewportWidth = window.innerWidth;
      const panAreaWidth = viewportWidth - 2 * padding;

      const imageWidth = zoomLevelObject.elementSize?.x;

      if (!imageWidth) return 1;

      const zoomCoefficient = panAreaWidth / imageWidth;

      return zoomCoefficient;
    },
  });

  lightbox.init();
  lightbox.loadAndOpen(0);

  lightbox.on('uiRegister', function () {
    lightbox.pswp.ui.registerElement({
      name: 'custom-caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      html: 'Caption text',
      onInit: (el, pswp) => {
        lightbox.pswp.on('change', () => {
          el.innerHTML = caption || '';
        });
      },
    });
  });

  lightbox.on('close', function (this: PhotoSwipeLightbox) {
    this.destroy();
    lightbox = null;
  });
};

function getPadding() {
  const width = window.innerWidth;
  if (width >= 1281) {
    return 252; // desktop
  } else if (width >= 768) {
    return 32; // tablet
  } else {
    return 16; // mobile
  }
}

const clickHandler = ({ target }: Event) => {
  const typedTarget = target as HTMLImageElement;

  const src = typedTarget.getAttribute('src');
  const width = typedTarget.getAttribute('width') ?? typedTarget.naturalWidth;
  const height =
    typedTarget.getAttribute('height') ?? typedTarget.naturalHeight;
  const caption = typedTarget.getAttribute('data-caption');

  if (src !== null) {
    viewImage({
      src,
      width: Number(width),
      height: Number(height),
      caption: caption ?? '',
    });
  }
};

const handleImage = (el: HTMLImageElement, caption: string) => {
  if (!(el instanceof HTMLImageElement)) return;

  el.style.cursor = 'zoom-in';
  el.setAttribute('data-caption', caption ?? '');
  el.addEventListener('click', clickHandler, { passive: true });
};

const removeClickEventListener = (el: HTMLImageElement) => {
  if (!(el instanceof HTMLImageElement)) return;

  el.removeEventListener('click', clickHandler);
};

export default <Directive<HTMLElement, Partial<{ caption: string }>>>{
  mounted(el, { value: { caption } }) {
    if (el instanceof HTMLImageElement) {
      handleImage(el, caption ?? '');
    } else if (el instanceof HTMLElement) {
      const images = el.querySelectorAll('img');

      images.forEach(el => {
        handleImage(el, caption ?? '');
      });
    }
  },

  unmounted(el) {
    if (el instanceof HTMLImageElement) {
      removeClickEventListener(el);
    } else if (el instanceof HTMLElement) {
      const images = el.querySelectorAll('img');

      images.forEach(el => {
        removeClickEventListener(el);
      });
    }
  },
};
